import { useEffect, useRef, useState } from "react";

export type CreditProgressBarMode = "fill" | "drain" | "static";

// Delay before starting the animation (allows component to mount)
const PRE_ANIMATION_DELAY_MS = 100;

export const FILL_BAR_ANIMATION_DURATION_MS = 600;
export const FILL_ICON_ANIMATION_DURATION_MS = 300;

const BAR_ANIMATION_DURATION_BY_MODE: Record<CreditProgressBarMode, number> = {
  fill: FILL_BAR_ANIMATION_DURATION_MS,
  drain: 1200,
  static: 0,
};

const ICON_ANIMATION_DURATION_BY_MODE: Record<CreditProgressBarMode, number> = {
  fill: FILL_ICON_ANIMATION_DURATION_MS,
  drain: 300,
  static: 0,
};

interface TransitionProps {
  transitionDuration: string;
  transitionTimingFunction: string;
  transitionDelay: string;
}

interface UseProgressBarAnimationResult {
  currentProgress: number;
  isCurrentProgressFull: boolean;
  isAnimationComplete: boolean;
  isIconLit: boolean;
  barTransitionProps: TransitionProps;
  iconTransitionProps: TransitionProps;
}

interface UseProgressBarAnimationParams {
  completedCount: number;
  requiredCount: number;
  mode: CreditProgressBarMode;
  /**
   * Optional callback fired when the animation completes (both bar and icon).
   * Not called for "static" mode.
   */
  onAnimationComplete?: () => void;
}

export function useProgressBarAnimation(
  params: UseProgressBarAnimationParams
): UseProgressBarAnimationResult {
  const { completedCount, requiredCount, mode, onAnimationComplete } = params;

  // requiredCount should always be a non-zero positive integer >= completedCount, but clamp it just in case.
  const targetProgress =
    requiredCount <= 0 ? 1 : Math.max(0, Math.min(completedCount / requiredCount, 1));
  const isTargetProgressFull = targetProgress === 1;

  // Track the current fullness of the progress bar, for animation purposes.
  const [currentProgress, setCurrentProgress] = useState(() => {
    switch (mode) {
      case "drain":
        // Start full and drain down.
        return 1;
      case "static":
        // Start at the final value and stay there.
        return targetProgress;
      case "fill":
        // Start empty and fill up.
        return 0;
    }
  });
  const isCurrentProgressFull = currentProgress === 1;

  const [isIconLit, setIsIconLit] = useState(() => {
    switch (mode) {
      case "drain":
        return true;
      case "static":
        return isTargetProgressFull;
      case "fill":
        return false;
    }
  });

  const [isAnimationComplete, setIsAnimationComplete] = useState(mode === "static");

  const barAnimationDurationMs = BAR_ANIMATION_DURATION_BY_MODE[mode];
  const iconAnimationDurationMs = ICON_ANIMATION_DURATION_BY_MODE[mode];
  const totalAnimationDurationMs = barAnimationDurationMs + iconAnimationDurationMs;

  // Trigger the animations after mount. CSS transition-delay handles the sequencing.
  useEffect(() => {
    if (mode === "static") {
      setCurrentProgress(targetProgress);
      setIsIconLit(isTargetProgressFull);
      setIsAnimationComplete(true);
      return;
    }

    // Reset animation state to the starting position for the new mode.
    // useState initializers only run on first mount, so when mode changes
    // (e.g. static → drain), we must explicitly set starting values.
    switch (mode) {
      case "fill":
        setCurrentProgress(0);
        setIsIconLit(false);
        break;
      case "drain":
        setCurrentProgress(1);
        setIsIconLit(true);
        break;
    }
    setIsAnimationComplete(false);

    const animationStartTimer = setTimeout(() => {
      setCurrentProgress(targetProgress);
    }, PRE_ANIMATION_DELAY_MS);

    const iconLitDelayMs =
      mode === "drain" ? PRE_ANIMATION_DELAY_MS : PRE_ANIMATION_DELAY_MS + totalAnimationDurationMs;

    const iconLitTimer = setTimeout(() => {
      setIsIconLit(isTargetProgressFull);
    }, iconLitDelayMs);

    return () => {
      clearTimeout(animationStartTimer);
      clearTimeout(iconLitTimer);
    };
  }, [mode, targetProgress, isTargetProgressFull, totalAnimationDurationMs]);

  // Fire onAnimationComplete after all animations finish.
  // Use a ref to avoid re-triggering the effect when the callback identity changes.
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  onAnimationCompleteRef.current = onAnimationComplete;

  useEffect(() => {
    if (mode === "static") {
      return;
    }

    const completeTimer = setTimeout(() => {
      setIsAnimationComplete(true);
      onAnimationCompleteRef.current?.();
    }, PRE_ANIMATION_DELAY_MS + totalAnimationDurationMs);

    return () => {
      clearTimeout(completeTimer);
    };
  }, [mode, totalAnimationDurationMs]);

  // In fill mode, the bar animates first, then the icon after. In drain mode, it's reversed.
  const barTransitionProps: TransitionProps = {
    transitionDuration: `${barAnimationDurationMs}ms`,
    transitionTimingFunction: "ease-out",
    transitionDelay: `${mode === "drain" ? iconAnimationDurationMs : 0}ms`,
  };
  const iconTransitionProps: TransitionProps = {
    transitionDuration: `${iconAnimationDurationMs}ms`,
    transitionTimingFunction: "ease-out",
    transitionDelay: `${mode === "fill" ? barAnimationDurationMs : 0}ms`,
  };

  return {
    currentProgress,
    isCurrentProgressFull,
    isAnimationComplete,
    isIconLit,
    barTransitionProps,
    iconTransitionProps,
  };
}
